"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import useGetToken from "@/hooks/useGetToken";
import { myAlert } from "@/lib/myAlert";
import clsx from "clsx";
import Cookies from "js-cookie"; // Import js-cookie
import {
  BellRing,
  Clock,
  Home,
  LogOut,
  NotepadText,
  UserRound,
} from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useLogoutMutation } from "../auth/api";
import { useGetProfile } from "../profile/api";
import { unsubscribeWebPush } from "../webpush/api";

const ProfileNav = ({ onlyDashboard = false }: { onlyDashboard?: boolean }) => {
  const { decode } = useGetToken();
  const userRole = decode?.role;
  const { data: User } = useGetProfile();
  const userData = User?.data;
  const isUser = userRole === "user";
  const pathname = usePathname();
  const router = useRouter();

  const profileUrl = isUser ? "/profile" : "/profile-admin";
  const notificationUrl = isUser ? "/notification" : "/notification-admin";
  const logActivityUrl = isUser ? "/log-activity" : "/log-activity-admin";

  const { mutate } = useLogoutMutation();

  const logout = () => {
    myAlert.confirm("Logout", "Yakin ingin keluar?", "Logout").then((res) => {
      if (!res) return;
      mutate(undefined, {
        onSuccess: async () => {
          Cookies.remove("accessToken");
          try {
            await unsubscribeWebPush();
          } finally {
            router.push("/login");
          }
        },
      });
    });
  };

  const navItems = [
    { name: "Profil", icon: <UserRound color="#2F5673" />, url: profileUrl },
    {
      name: "Notifikasi",
      icon: <BellRing color="#2F5673" />,
      url: notificationUrl,
    },
    {
      name: "Riwayat Usulan",
      icon: <NotepadText color="#2F5673" />,
      url: "/aspiration-history",
    },
    {
      name: "Log Aktivitas",
      icon: <Clock color="#2F5673" />,
      url: logActivityUrl,
    },
  ];

  const profileImage = userData?.imageUrl || decode?.imageUrl;
  const profileName = userData?.name || decode?.name;

  return (
    <div className="wrap ml-4 flex gap-3 items-center">
      <DropdownMenu>
        <DropdownMenuTrigger>
          <div className="flex items-center gap-3 text-white">
            <Avatar>
              <AvatarImage src={profileImage} alt={profileName} />
              <AvatarFallback>{profileName?.charAt(0) ?? "U"}</AvatarFallback>
            </Avatar>
            <div className="text-sm text-black flex items-end flex-col">
              <div className="text-base font-semibold text-black-900 hidden lg:block">
                {profileName}
              </div>
            </div>
          </div>
        </DropdownMenuTrigger>

        <DropdownMenuContent className="bg-white mt-2 w-[180px] mr-3">
          {!onlyDashboard &&
            navItems
              .filter((item) => {
                // Tampilkan "Riwayat Usulan" hanya jika role-nya "user"
                if (item.name === "Riwayat Usulan" && !isUser) return false;
                return true;
              })
              .map((item) => (
                <DropdownMenuItem key={item.name}>
                  <Link
                    className={clsx(
                      "w-full flex items-center gap-2 text-black",
                      {
                        "font-bold text-secondary": pathname.includes(item.url),
                      }
                    )}
                    href={item.url}
                  >
                    <div className="flex justify-center items-center w-[35px] h-[35px] rounded-full bg-[#EBE4D6]">
                      {item.icon}
                    </div>
                    {item.name}
                  </Link>
                </DropdownMenuItem>
              ))}

          {onlyDashboard && (
            <DropdownMenuItem>
              <Link
                className={clsx("w-full flex items-center gap-2 text-black")}
                href={"/dashboard"}
              >
                <div className="flex justify-center items-center w-[35px] h-[35px] rounded-full bg-[#EBE4D6]">
                  <Home />
                </div>
                Dashboard
              </Link>
            </DropdownMenuItem>
          )}

          <DropdownMenuItem onClick={logout} className="cursor-pointer">
            <div className="flex items-center gap-2">
              <div className="flex justify-center items-center flex-shrink-0 w-[35px] h-[35px] rounded-full bg-[#FFC3C3]">
                <LogOut color="#D24545" />
              </div>
              <div className="w-full text-red-500">Keluar</div>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default ProfileNav;
